import { Events } from '@Context/common/general/context';
import { useApi } from '@hooks';
import useGlobalContext from '@hooks/common/general/use-global-context';
import AccountRoles from '@Shared/enums/AccountRoles';
import { Api } from '@Shared/schemas';
import { AccountUserRecord } from '@Shared/schemas/accounts/accountSchemas';
import { UserRecord } from '@Shared/schemas/users/userSchemas';
import { getRoleLevel } from '@Shared/utils/rolePermissions';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

// Combined type for member records from the API
type MemberRecord = UserRecord & AccountUserRecord;

interface ProfileSelectionContextType {
  searchTerm: string;
  profiles: UserRecord[];
  filteredProfiles: UserRecord[];
  manyProfiles: boolean;
  setSearchTerm: (term: string) => void;
  handleProfileSelect: (profileId: string) => void;
  handleClearSearch: () => void;
  handleAddProfile: () => void;
  handleInviteUser: () => void;
  isLoadingAccounts: boolean;
}

const ProfileSelectionContext = createContext<ProfileSelectionContextType | undefined>(undefined);

interface ProfileSelectionProviderProps {
  children: ReactNode;
}

export const ProfileSelectionProvider = ({ children }: ProfileSelectionProviderProps) => {
  const { globalState } = useGlobalContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [profiles, setProfiles] = useState<UserRecord[]>([]);
  const [userRoleLevel, setUserRoleLevel] = useState(0);
  const manyProfiles = profiles.length > 4;

  const { call: getAccounts, loading: isLoadingAccounts } = useApi({
    api: Api.Account.GetAccounts,
    request: {
      resolve_members: true,
      account_ids: globalState.account?.id ? [globalState.account.id] : undefined,
    },
    onSuccess: (response) => {
      if (response.data?.[0]?.members && response.data[0].member_records) {
        const members = response.data[0].members;
        const memberRecords = response.data[0].member_records;

        // Sort member records by last name
        const sortedMembers = memberRecords.sort((a, b) => (a.last_name > b.last_name ? 1 : -1));
        setProfiles(sortedMembers);

        // Find current user's member record and set role level
        const currentUserMember = members.find((member) => member.user_id === globalState.user?.dbId);
        if (currentUserMember?.role_id) {
          // Convert role_id to AccountRoles enum value
          const roleId = Object.values(AccountRoles).find(
            (role) => typeof role === 'number' && role === currentUserMember.role_id
          );
          if (roleId) {
            const roleLevel = getRoleLevel(roleId);
            setUserRoleLevel(roleLevel);
          }
        }
      }
    },
  });

  const handleProfileSelect = (profileId: string) => {
    if (profileId === globalState.user?.id) {
      Events.app.navigate.emit({ path: '/' });
    } else {
      Events.app.navigate.emit({ path: `/select-profile/pin-entry/${profileId}` });
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleAddProfile = () => {
    Events.app.navigate.emit({ path: '/select-profile/add' });
  };

  const handleInviteUser = () => {
    Events.app.navigate.emit({ path: '/select-profile/invite' });
  };

  const filteredProfiles = profiles.filter((profile) => {
    const searchTermLower = searchTerm.toLowerCase();
    const fullName = `${profile.first_name} ${profile.last_name}`.toLowerCase();
    return fullName.includes(searchTermLower);
  });

  useEffect(() => {
    getAccounts();
  }, []);

  const value = {
    searchTerm,
    profiles,
    filteredProfiles,
    manyProfiles,
    setSearchTerm,
    handleProfileSelect,
    handleClearSearch,
    handleAddProfile,
    handleInviteUser,
    isLoadingAccounts,
  };

  return <ProfileSelectionContext.Provider value={value}>{children}</ProfileSelectionContext.Provider>;
};

export const useProfileSelection = () => {
  const context = useContext(ProfileSelectionContext);
  if (context === undefined) {
    throw new Error('useProfileSelection must be used within a ProfileSelectionProvider');
  }
  return context;
};
